import { QueryClient, useQuery } from '@tanstack/react-query';
import { countersQuery, slotLabelsQuery } from '@youshift/shared/hooks/queries';
import { CustomCounter, SlotLabel } from '@youshift/shared/types';
import { createContext, useContext } from 'react';
import { Outlet, useLoaderData, useParams } from 'react-router-dom';
import { preLoadQuery } from '@youshift/shared/hooks';

// Define the context type
export type ChainContextType = {
  slotLabels: Record<number, SlotLabel>;
  counters: Record<number, CustomCounter>;
};

// Create the context
export const ChainContext = createContext<ChainContextType | undefined>(
  undefined,
);

// Create a hook to use the context
export function useChainContext() {
  const context = useContext(ChainContext);
  if (!context) {
    throw new Error('useChainContext must be used within a ChainProvider');
  }
  return context;
}

// Define the loader type
type ChainLoader = {
  slotLabels: Record<number, SlotLabel>;
  counters: Record<number, CustomCounter>;
};

// Create the loader function
export const chainLoader =
  (queryClient: QueryClient) =>
  async ({ params }: { params: { idChain: string } }): Promise<ChainLoader> => {
    const slotLabels = await preLoadQuery(
      queryClient,
      slotLabelsQuery(Number(params.idChain)),
    );
    const counters = await preLoadQuery(
      queryClient,
      countersQuery(Number(params.idChain)),
    );
    return {
      slotLabels,
      counters,
    };
  };

// Create a hook to use the loader data
export const useChainLoader = (): ChainContextType => {
  const { idChain } = useParams();
  const initialData = useLoaderData() as ChainLoader;

  const { data: slotLabels } = useQuery({
    ...slotLabelsQuery(Number(idChain)),
    initialData: initialData.slotLabels,
  });
  const { data: counters } = useQuery({
    ...countersQuery(Number(idChain)),
    initialData: initialData.counters,
  });

  return {
    slotLabels,
    counters,
  };
};

// Create the layout component
export default function ChainLayout(): JSX.Element {
  const contextValue = useChainLoader();

  return (
    <ChainContext.Provider value={contextValue}>
      <Outlet />
    </ChainContext.Provider>
  );
}
